import React, { useState, useEffect } from "react";

const CustomInput = ({
  type = "text",
  placeholder = "Enter text",
  label = "Field label",
  title = null,
  description = null,
  onChange,
  onEnter,
  value,
  extraLabel = false,
  border = false,
  validateLength = false,
  maxLength = 24,
  setIsButtonDisabled = null,
  textError,
  setTextError,
  requiredError = false, // New prop for required field error
  setRequiredError, // New prop for setting required error
}) => {
  // Conditionally use context only if available (for pages outside book creation)
  let authorName, setAuthorName, email, setEmail;
  try {
    const context = useBookCreation();
    authorName = context.authorName;
    setAuthorName = context.setAuthorName;
    email = context.authorEmail;
    setEmail = context.setAuthorEmail;
  } catch (error) {
    // Context not available, use local state instead
    authorName = '';
    setAuthorName = () => {};
    email = '';
    setEmail = () => {};
  }
  const [showPassword, setShowPassword] = useState(false);
  const inputType = type === "password" ? (showPassword ? "text" : "password") : type;

  const [focus, setFocus] = useState(false);
  const [localPlaceholder, setLocalPlaceholder] = useState(placeholder);

  const handleChange = (e) => {
    const newValue = e.target.value;

    // Clear required error when user starts typing
    if (requiredError && setRequiredError && newValue.trim()) {
      setRequiredError(false);
    }

    if (type === "email" && setEmail) {
      setEmail(newValue);
    } else if (type === "text" && setAuthorName) {
      setAuthorName(newValue);
    }

    if (validateLength && type === "text") {
      newValue.length > maxLength ? setTextError(true) : setTextError(false);
    }

    if (onChange) onChange(newValue);
  };

  const handleFocus = () => {
    setFocus(true);
    setLocalPlaceholder("");
    
    // Clear required error on focus
    if (requiredError && setRequiredError) {
      setRequiredError(false);
    }
  };

  const handleBlur = () => {
    setFocus(false);
    setLocalPlaceholder(placeholder);
  };

  return (
    <div className="w-full mb-8">
      <div className="flex items-center space-x-2">
        <p className="field-title" >{title}</p>
      </div>
      <div className="field-desc" dangerouslySetInnerHTML={{ __html: description }}></div>
      {extraLabel && (
        <label className=" block text-gray-300   -mb-1">{label}</label>
      )}

      <div className="relative">
        {(value || focus) && (
          <label
            className={`absolute ${focus || value ? 'top-[23px]' : 'top-2'} left-4 text-[#8F8F8F] text-[12.5px] transition-all `}
          >
            {label}
          </label>
        )}

        {/* Length validation error */}
        {textError && validateLength && type === "text" && (
          <div className="absolute md:top-[45%] left-0 md:left-auto md:right-4 text-[#DD4E4E] md:text-[15px] flex items-center text-[12px] top-[105%]">
            <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.97372 18.0001L11.5 1.50012L21.0263 18.0001H1.97372Z" stroke="#DD4E4E" strokeWidth="0.5" />
              <path d="M12.3763 6.78271L12.2863 13.4075H11.2422L11.1521 6.78271H12.3763ZM11.7642 16.0719C11.5422 16.0719 11.3517 15.9924 11.1927 15.8334C11.0336 15.6743 10.9541 15.4838 10.9541 15.2618C10.9541 15.0398 11.0336 14.8492 11.1927 14.6902C11.3517 14.5312 11.5422 14.4517 11.7642 14.4517C11.9863 14.4517 12.1768 14.5312 12.3358 14.6902C12.4948 14.8492 12.5743 15.0398 12.5743 15.2618C12.5743 15.4088 12.5368 15.5438 12.4618 15.6668C12.3898 15.7899 12.2923 15.8889 12.1693 15.9639C12.0493 16.0359 11.9142 16.0719 11.7642 16.0719Z" fill="#DD4E4E" />
            </svg>
            The Name is too long, we recommend using a shorter one
          </div>
        )}

  

        <input
          type={inputType}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`mt-[14px] rounded-[7px]  ${focus || value ? 'pb-[9px] pt-7' : 'pt-2 pb-2'} ${border ? "border" : ""} ${requiredError ? "border-red-500 border" : "border-[0.3px] border-[#bfbfbf]/50"} px-4 w-full h-[60px] focus:outline-none text-[17px]`}
          placeholder={value ? "" : localPlaceholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter?.(e.target.value);
            }
          }}
        />
              {/* Required field error */}
        {requiredError && (
          <div className="md:top-[100%] left-0 md:left-auto md:right-4 text-red-500 md:text-[12px] flex items-center text-[12px] top-[105%]">

            The field is required
          </div>
        )}
        {type === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 mt-2 text-[#bfbfbf]"
            tabIndex={-1}
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#bfbfbf"><path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200Zm0-300Zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280Z" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#bfbfbf"><path d="m644-428-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428Zm128 126-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302Zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56ZM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82Zm319 93Zm-151 75Z" /></svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomInput;